import { createTRPCReact, httpBatchLink } from "@trpc/react-query";
import { QueryClient } from "@tanstack/react-query";
import superjson from "superjson";
import type { AppRouter } from "@server/trpc/router";

export const trpc = createTRPCReact<AppRouter>();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Cap any single tRPC request so the UI can't get stuck on `isPending` when
// the connection dies mid-flight (network drop, proxy killing a slow Claude
// call). A single AI body call is bounded at 120s server-side, and brief
// regeneration can chain brief + body in one request, so allow 180s for the
// slowest legitimate call (plus DB write + response) while still surfacing
// genuinely dead requests as errors. Stays well under Cloud Run's 300s cap.
const TRPC_REQUEST_TIMEOUT_MS = 180_000;

function fetchWithTimeout(url: RequestInfo | URL, opts?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new Error(`Request timed out after ${TRPC_REQUEST_TIMEOUT_MS}ms`));
  }, TRPC_REQUEST_TIMEOUT_MS);

  const upstream = opts?.signal;
  if (upstream) {
    if (upstream.aborted) controller.abort(upstream.reason);
    else upstream.addEventListener("abort", () => controller.abort(upstream.reason), { once: true });
  }

  return fetch(url, { ...opts, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
  });
}

export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch: fetchWithTimeout,
    }),
  ],
});
